import {Component, DestroyRef, inject, Input, OnChanges, SimpleChanges, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, FormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {BillableItemType, FlatFeeType} from '@ee/common/enums';
import {CountyFeeSettingsService, OrganizationService} from '@ee/common/services';
import {Observable, Subject, combineLatest, map, startWith, takeUntil} from 'rxjs';
import {County, CountyFeeSetting, Court, Tenant, BillableItem, TeamMember, BillableItemCategory, CheckPayee, SearchSuggestionModel} from '@ee/common/models';

@Component({
  selector: 'ee-billing-form',
  template: `
    <div [formGroup]="billingForm" class="flex flex-col">
      <div class="flex flex-row mb-2 justify-center items-stretch">
        <mat-form-field class="compact flex-1" [class.mr-4]="!isWorkflowConfig" appearance="outline" subscriptSizing="fixed">
          <mat-label>Billing Entry Type</mat-label>
          <mat-select required formControlName="type">
            <mat-option [value]="billableItemType.FLAT_FEE">Flat Fee</mat-option>
            <mat-option [value]="billableItemType.TIME_ENTRY">Time Entry</mat-option>
            <mat-option [value]="billableItemType.EXPENSE">Expense</mat-option>
          </mat-select>
        </mat-form-field>
        @if (!isWorkflowConfig) {
          <mat-form-field class="compact flex-1" [class.mr-4]="showPerformedBy" subscriptSizing="fixed" appearance="outline">
            <mat-label>Date Incurred</mat-label>
            <input matInput [matDatepicker]="billingPicker" formControlName="date_incurred">
            <mat-datepicker-toggle matSuffix [for]="billingPicker"></mat-datepicker-toggle>
            <mat-datepicker #billingPicker></mat-datepicker>
          </mat-form-field>
        }
        @if (showPerformedBy) {
          <mat-form-field class="compact flex-1" appearance="outline" subscriptSizing="fixed">
            <mat-label>Performed By</mat-label>
            <mat-select [value]="billingForm.get('performed_by_user_id')?.value"
                        (selectionChange)="onPerformedByChange($event.value)">
              @for (member of teamMembers; track member.id) {
                <mat-option [value]="member.id">{{ member.display_name }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
        }
      </div>
      <div class="flex flex-row mb-2 justify-center items-stretch">
        @if (billingForm.get('type').value === billableItemType.EXPENSE) {
          <mat-form-field class="flex-1 compact mr-4" subscriptSizing="fixed" appearance="outline">
            <mat-label>Category</mat-label>
            <input matInput #expenseCategoryInput
              [formControl]="categorySearchCtrl"
              [matAutocomplete]="expenseCategoryAuto"
              (blur)="onCategoryBlur()">
            @if (billingForm.get('category_id')?.value) {
              <button matSuffix type="button" class="clear-category-btn" (click)="clearCategory(); $event.stopPropagation()" aria-label="Clear category">
                <mat-icon class="clear-category-icon">close</mat-icon>
              </button>
            }
            <mat-autocomplete #expenseCategoryAuto="matAutocomplete"
              [displayWith]="displayCategoryName.bind(this)"
              (optionSelected)="onCategorySelected($event.option.value)">
              @for (cat of filteredExpenseCategories; track cat.id) {
                <mat-option [value]="cat.id">
                  <span [innerHTML]="cat.name | highlightSearch:expenseCategoryInput.value"></span>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }
        @if (billingForm.get('type').value === billableItemType.FLAT_FEE) {
          <mat-form-field class="flex-1 compact mr-4" subscriptSizing="fixed" appearance="outline">
            <mat-label>Category</mat-label>
            <input matInput #flatFeeCategoryInput
              [formControl]="categorySearchCtrl"
              [matAutocomplete]="flatFeeCategoryAuto"
              (blur)="onCategoryBlur()">
            <mat-autocomplete #flatFeeCategoryAuto="matAutocomplete"
              [displayWith]="displayCategoryName.bind(this)"
              (optionSelected)="onCategorySelected($event.option.value)">
              @for (cat of filteredFlatFeeCategories; track cat.id) {
                <mat-option [value]="cat.id">
                  <span [innerHTML]="cat.name | highlightSearch:flatFeeCategoryInput.value"></span>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }
        @if (billingForm.get('type').value === billableItemType.TIME_ENTRY) {
          <mat-form-field class="flex-1 compact mr-4" subscriptSizing="fixed" appearance="outline">
            <mat-label>Category</mat-label>
            <input matInput #timeEntryCategoryInput
              [formControl]="categorySearchCtrl"
              [matAutocomplete]="timeEntryCategoryAuto"
              (blur)="onCategoryBlur()">
            @if (billingForm.get('category_id')?.value) {
              <button matSuffix type="button" class="clear-category-btn" (click)="clearCategory(); $event.stopPropagation()" aria-label="Clear category">
                <mat-icon class="clear-category-icon">close</mat-icon>
              </button>
            }
            <mat-autocomplete #timeEntryCategoryAuto="matAutocomplete"
              [displayWith]="displayCategoryName.bind(this)"
              (optionSelected)="onCategorySelected($event.option.value)">
              @for (cat of filteredTimeEntryCategories; track cat.id) {
                <mat-option [value]="cat.id">
                  <span [innerHTML]="cat.name | highlightSearch:timeEntryCategoryInput.value"></span>
                </mat-option>
              }
            </mat-autocomplete>
          </mat-form-field>
        }
        @if (billingForm.get('type').value === billableItemType.FLAT_FEE && isLegacyMode && effectiveLegacyAllowed) {
          <mat-form-field class="flex-1 compact mr-4" subscriptSizing="fixed" appearance="outline">
            <mat-label>Flat Fee Type</mat-label>
            <mat-select formControlName="flat_fee_type">
              <mat-option [value]="flatFeeType.CUSTOM">Custom</mat-option>
              @if (attorneyFee || isWorkflowConfig) {
                <mat-option [value]="flatFeeType.CLIENT_ATTORNEY_FEE">
                  Client-Specific Attorney Fee
                </mat-option>
              }
              @if ((county && countyFeeSetting) || setupForm) {
                <mat-option [value]="flatFeeType.COUNTY_FEE">
                  Calculated - County Fees
                </mat-option>
                <mat-option [value]="flatFeeType.SHERIFF_FEE">
                  Calculated - Sheriff Fee
                </mat-option>
                <mat-option [value]="flatFeeType.WRIT_FEE">
                  Calculated - Writ Filing Fee
                </mat-option>
                <mat-option [value]="flatFeeType.HEARING_FEE">
                  Calculated - Hearing Fee
                </mat-option>
              }
            </mat-select>
            @if (setupForm && (billingForm.get('flat_fee_type').value === flatFeeType.COUNTY_FEE || billingForm.get('flat_fee_type').value === flatFeeType.WRIT_FEE || billingForm.get('flat_fee_type').value === flatFeeType.SHERIFF_FEE || billingForm.get('flat_fee_type').value === flatFeeType.HEARING_FEE)) {
              <mat-hint>
                *More information below
              </mat-hint>
            }
          </mat-form-field>
        }
        @if (billingForm.get('type').value !== billableItemType.TIME_ENTRY) {
          <mat-form-field class="compact flex-1" subscriptSizing="fixed" appearance="outline">
            <span matPrefix>$ &nbsp;</span>
            <mat-label>Amount</mat-label>
            <input matInput type="number" formControlName="amount">
          </mat-form-field>
        }
        @if (billingForm.get('type').value === billableItemType.TIME_ENTRY) {
          <mat-form-field class="compact flex-1 mr-4" subscriptSizing="fixed" appearance="outline">
            <mat-label>Hours</mat-label>
            <input matInput type="number" formControlName="qty">
          </mat-form-field>
        }
        @if (billingForm.get('type').value === billableItemType.TIME_ENTRY) {
          <mat-form-field class="compact flex-1" subscriptSizing="fixed" appearance="outline">
            <span matPrefix>$ &nbsp;</span>
            <mat-label>Rate</mat-label>
            <input matInput type="number" formControlName="rate">
          </mat-form-field>
        }
      </div>
      @if (allowCategoryCostTypeOverride && !isLegacyMode && billingForm.get('category_id')?.value) {
        <div class="flex flex-row mb-2 items-center">
          <span class="cost-type-label">Cost Type</span>
          <mat-button-toggle-group [value]="billingForm.get('cost_type')?.value ?? COST_TYPE_NONE"
            (change)="onCostTypeToggleChange($event.value)"
            appearance="standard"
            class="cost-type-toggle">
            <mat-button-toggle [value]="COST_TYPE_NONE">None</mat-button-toggle>
            <mat-button-toggle value="HARD">Hard</mat-button-toggle>
            <mat-button-toggle value="SOFT">Soft</mat-button-toggle>
          </mat-button-toggle-group>
        </div>
      }
      @if (allowSplitDuplicate) {
        <div class="split-duplicate-section flex flex-col mb-4">
          <mat-button-toggle-group formControlName="split_mode" appearance="standard" class="split-mode-toggle"
            (change)="onSplitModeChange($event.value)">
            @if (splitDuplicateContext === 'case') {
              <mat-button-toggle [value]="SPLIT_MODE_THIS_CASE">This case only</mat-button-toggle>
            }
            <mat-button-toggle [value]="SPLIT_MODE_SPLIT">Split across cases</mat-button-toggle>
            <mat-button-toggle [value]="SPLIT_MODE_DUPLICATE">Duplicate to cases</mat-button-toggle>
          </mat-button-toggle-group>

          @if (showCasePicker) {
            <ee-eviction-search-select
              [label]="splitDuplicateContext === 'grid' ? 'Cases' : 'Add target cases'"
              placeholder="Search cases by ID, client, or property..."
              [multiSelect]="true"
              [fixedChipLabel]="splitDuplicateContext === 'case' ? 'This case (current)' : null"
              [initialCaseIds]="splitDuplicateContext === 'grid' ? initialTargetCaseIds : []"
              [excludeCaseIds]="pickerExcludeCaseIds"
              (selectionsChange)="onTargetsChange($any($event))"></ee-eviction-search-select>
          }

          @if (isSplitMode && splitTargetCount > 0) {
            <div class="split-breakdown">
              @if (isTimeEntrySplit) {
                <span>Total: {{ splitTotal }} hrs &divide; {{ splitPartsCount }} cases = {{ splitPerCase }} hrs each</span>
              } @else {
                <span>Total: {{ splitTotal | currency }} &divide; {{ splitPartsCount }} cases = {{ splitPerCase | currency }} each</span>
              }
              @if (!splitDividesEvenly) {
                <div class="split-rounding-note">Rounding applied — totals sum to the original amount.</div>
              }
            </div>
          }
        </div>
      }
      <div class="flex flex-row justify-stretch items-center mb-4">
        <mat-form-field class="compact flex-1" [class.mr-4]="!setupForm" appearance="outline">
          <mat-label>Description</mat-label>
          <input #billingDescriptionInput matInput formControlName="description"
            [matAutocomplete]="billingDescriptionAutoComplete"
            [matAutocompleteDisabled]="!filteredDescriptions$ || (filteredDescriptions$ | async)?.length < 1">
            @if (setupForm && (billingForm.get('flat_fee_type').value === flatFeeType.COUNTY_FEE || billingForm.get('flat_fee_type').value === flatFeeType.WRIT_FEE || billingForm.get('flat_fee_type').value === flatFeeType.SHERIFF_FEE || billingForm.get('flat_fee_type').value === flatFeeType.HEARING_FEE)) {
              <mat-hint>
                [COUNTY] will be overridden with the actual county name
              </mat-hint>
            }
          </mat-form-field>
          <mat-autocomplete #billingDescriptionAutoComplete="matAutocomplete">
            @for (option of (filteredDescriptions$ | async); track option) {
              <mat-option [value]="option">
                <span [innerHTML]="option | highlightSearch:billingDescriptionInput.value"></span>
              </mat-option>
            }
            <mat-option [value]="billingDescriptionInput.value">Add New Description</mat-option>
          </mat-autocomplete>
          @if (!setupForm) {
            <mat-checkbox formControlName="save_description">Save description</mat-checkbox>
          }
        </div>
        @if (setupForm && (billingForm.get('flat_fee_type').value === flatFeeType.COUNTY_FEE || billingForm.get('flat_fee_type').value === flatFeeType.WRIT_FEE || billingForm.get('flat_fee_type').value === flatFeeType.SHERIFF_FEE || billingForm.get('flat_fee_type').value === flatFeeType.HEARING_FEE)) {
          <div>
            *County-specific billable items will only apply when a valid county has been selected and an
            applicable county fee has been set up.
          </div>
        }

        <!-- Check Request Section -->
        @if (billingForm.get('request_check')) {
          <div class="border-t border-gray-200">
            <mat-checkbox formControlName="request_check" class="mb-4">Request Check</mat-checkbox>

            @if (billingForm.get('request_check').value && !isWorkflowConfig) {
              <div class="flex flex-row mb-2 justify-center items-stretch">
                <mat-form-field class="compact flex-1" appearance="outline" subscriptSizing="fixed">
                  <mat-label>Payee</mat-label>
                  <input matInput #payeeInput
                    data-testid="check-payee-input"
                    [formControl]="payeeSearchCtrl"
                    [matAutocomplete]="payeeAuto"
                    (blur)="onPayeeBlur()">
                  <mat-autocomplete #payeeAuto="matAutocomplete"
                    [displayWith]="displayPayeeName.bind(this)"
                    (optionSelected)="onPayeeSelected($event.option.value)">
                    @for (payee of filteredPayees; track payee.id) {
                      <mat-option [value]="payee.id">
                        <span [innerHTML]="payee.name | highlightSearch:payeeInput.value"></span>
                      </mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>
                @if (billableItemData?.check_entry_id) {
                  <div class="flex-1 flex items-center ml-4">
                    <a [routerLink]="'/payments/register'"
                       [queryParams]="{ check_entry_id: billableItemData.check_entry_id }"
                       class="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                       tp="View check in register">
                      Check Entry
                    </a>
                  </div>
                }
              </div>
              <div class="flex flex-row mb-2 justify-center items-stretch">
                <mat-form-field class="compact flex-1 mr-4" appearance="outline" subscriptSizing="fixed">
                  <mat-label>Check Processed Date</mat-label>
                  <input matInput [matDatepicker]="checkProcessedPicker" formControlName="check_processed_date">
                  <mat-datepicker-toggle matSuffix [for]="checkProcessedPicker"></mat-datepicker-toggle>
                  <mat-datepicker #checkProcessedPicker></mat-datepicker>
                </mat-form-field>
                <mat-form-field class="compact flex-1" appearance="outline" subscriptSizing="fixed">
                  <mat-label>Processed By</mat-label>
                  <input matInput formControlName="check_processed_by">
                </mat-form-field>
              </div>
              <div class="flex flex-row mb-2 items-center">
                <mat-checkbox formControlName="check_denied">Check Denied</mat-checkbox>
              </div>
            }
          </div>
        }
      </div>
    `,
  styles: [`
    mat-form-field.mw-200 {
      width: 200px;
      margin-right: 1rem;
    }
    ee-billing-form .cost-type-label {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.6);
      margin-right: 8px;
    }
    ee-billing-form .cost-type-toggle .mat-button-toggle-label-content {
      line-height: 32px;
      padding: 0 10px;
      font-size: 13px;
    }
    ee-billing-form .clear-category-btn {
      background: transparent;
      border: 0;
      padding: 0;
      margin-right: 4px;
      width: 20px;
      height: 20px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: rgba(0, 0, 0, 0.54);
      border-radius: 50%;
    }
    ee-billing-form .clear-category-btn:hover {
      color: rgba(0, 0, 0, 0.87);
      background: rgba(0, 0, 0, 0.06);
    }
    ee-billing-form .clear-category-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      line-height: 16px;
    }
    ee-billing-form .split-duplicate-section {
      border-top: 1px solid rgba(0, 0, 0, 0.08);
      margin-top: 8px;
      padding-top: 16px;
      gap: 14px;
    }
    ee-billing-form .split-mode-toggle .mat-button-toggle-button {
      display: flex;
      align-items: center;
    }
    ee-billing-form .split-mode-toggle .mat-button-toggle-label-content {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      min-height: 32px;
      line-height: 1;
      padding: 0 12px;
      font-size: 13px;
    }
    ee-billing-form .split-breakdown {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.72);
    }
    ee-billing-form .split-rounding-note {
      font-size: 12px;
      font-style: italic;
      color: rgba(0, 0, 0, 0.54);
    }
  `],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class BillingFormComponent implements OnChanges {
  private destroyRef = inject(DestroyRef);
  private organizationService = inject(OrganizationService);
  private countyFeeSettingsService = inject(CountyFeeSettingsService);

  billableItemType = BillableItemType;
  flatFeeType = FlatFeeType;

  /**
   * Sentinel used by the per-item cost-type override toggle. Mat-button-toggle-group matches
   * selections by strict `[value]` equality, which means null can't round-trip back to an
   * option. We surface "None" as the string 'NONE' and map it to/from null at the form boundary.
   */
  readonly COST_TYPE_NONE = 'NONE';

  @Input() billingForm: UntypedFormGroup;
  @Input() setupForm = false;
  @Input() tenants: Tenant[];
  @Input() county: County;
  @Input() court: Court;
  @Input() attorneyFee: number | undefined;
  @Input() isWorkflowConfig = false;
  @Input() billableItemData: BillableItem | null = null;
  @Input() teamMembers: TeamMember[] = [];
  @Input() showPerformedBy = false;
  @Input() categories: BillableItemCategory[] = [];
  @Input() allowCategoryCostTypeOverride = false;
  /**
   * Whether "Legacy" billing (the `builtin-legacy` category + the Flat Fee Type dropdown) is offered.
   * Defaults true so existing orgs (and hosts that don't pass it) are unaffected. When false, Legacy is
   * dropped from the category list, new flat fees default to Client Charge, the Flat Fee Type dropdown
   * is hidden, and a category becomes required — except when editing an item already stored as Legacy
   * (see `effectiveLegacyAllowed`), which must still display correctly (no data loss).
   */
  @Input() legacyBillingAllowed = true;
  @Input() payees: CheckPayee[] = [];

  /**
   * Enables the Split/Duplicate mode selector + multi-case picker (EE-181). Only the case-level
   * Add Billable Item form opts in; every other host (auto-billable dialog, editor dialog, check
   * queue) leaves it false so the controls stay hidden.
   */
  @Input() allowSplitDuplicate = false;
  /** The case the form is open on — implicitly included in split/duplicate and excluded from search. */
  @Input() currentCaseId: string | null = null;
  /**
   * `'case'` (default): opened on a single case — offers "This case only" and treats the current
   * case as an implicit part of the split. `'grid'`: opened over a multi-case selection (e.g. the
   * Open Evictions grid) — no "This case only", no implicit current case; every selected case is a
   * target and the split divides across exactly the selected cases.
   */
  @Input() splitDuplicateContext: 'case' | 'grid' = 'case';
  /** Case ids to pre-populate the target picker with (grid context). */
  @Input() initialTargetCaseIds: string[] = [];

  readonly SPLIT_MODE_THIS_CASE = 'THIS_CASE';
  readonly SPLIT_MODE_SPLIT = 'SPLIT';
  readonly SPLIT_MODE_DUPLICATE = 'DUPLICATE';

  countyFeeSetting: CountyFeeSetting;
  private allDescriptions$: Observable<string[]>;
  filteredDescriptions$: Observable<string[]>;

  /** Search control for the category autocomplete */
  categorySearchCtrl = new FormControl<string>('');
  filteredFlatFeeCategories: BillableItemCategory[] = [];
  filteredExpenseCategories: BillableItemCategory[] = [];
  filteredTimeEntryCategories: BillableItemCategory[] = [];

  /** Search control for the payee autocomplete */
  payeeSearchCtrl = new FormControl<string>('');
  filteredPayees: CheckPayee[] = [];

  private static readonly LEGACY_CATEGORY_ID = 'builtin-legacy';
  /** Generic default category for new flat fees when Legacy is disabled. */
  private static readonly DEFAULT_CATEGORY_ID = 'builtin-client-charge';

  /** True during initial form setup — prevents resetting cost_type on existing items */
  private initializing = false;

  /** True when the selected category is Legacy (or no category selected on a flat fee) */
  get isLegacyMode(): boolean {
    const categoryId = this.billingForm?.get('category_id')?.value;
    return !categoryId || categoryId === BillingFormComponent.LEGACY_CATEGORY_ID;
  }

  /**
   * True when the item currently being edited was already stored as a Legacy *flat fee*. Disabling
   * Legacy is a new-entry rule only — such an item must keep its Flat Fee Type dropdown and category
   * option so it renders without data loss (Requirement 4.5). Scoped to `FLAT_FEE` so an existing
   * uncategorized expense/time entry does NOT open the Legacy escape hatch (e.g. switching its type to
   * Flat Fee must still follow the disabled-org rules).
   */
  private get editingExistingLegacyItem(): boolean {
    if (!this.billableItemData || this.billableItemData.type !== BillableItemType.FLAT_FEE) return false;
    const existing = this.billableItemData.category_id;
    return !existing || existing === BillingFormComponent.LEGACY_CATEGORY_ID;
  }

  /** Legacy is offered when the org allows it OR when editing an item that is already Legacy. */
  get effectiveLegacyAllowed(): boolean {
    return this.legacyBillingAllowed || this.editingExistingLegacyItem;
  }

  // ── Split / duplicate (EE-181) ──────────────────────────────────────────

  get splitMode(): string {
    return this.billingForm?.get('split_mode')?.value ?? this.SPLIT_MODE_THIS_CASE;
  }

  /** Whether to show the target-case picker (a non-"this case only" mode is active). */
  get showCasePicker(): boolean {
    return this.allowSplitDuplicate && this.splitMode !== this.SPLIT_MODE_THIS_CASE;
  }

  get isSplitMode(): boolean {
    return this.allowSplitDuplicate && this.splitMode === this.SPLIT_MODE_SPLIT;
  }

  get pickerExcludeCaseIds(): string[] {
    return this.currentCaseId ? [this.currentCaseId] : [];
  }

  get splitTargetCount(): number {
    return (this.billingForm?.get('target_case_ids')?.value as string[] | null)?.length ?? 0;
  }

  /**
   * Cases the total is divided across. In `case` context that's the current case plus each target;
   * in `grid` context every selected case is a target, so it's just the target count.
   */
  get splitPartsCount(): number {
    return this.splitDuplicateContext === 'grid' ? this.splitTargetCount : this.splitTargetCount + 1;
  }

  get isTimeEntrySplit(): boolean {
    return this.billingForm?.get('type')?.value === BillableItemType.TIME_ENTRY;
  }

  /** The total being divided — hours for a time entry, otherwise the dollar amount. */
  get splitTotal(): number {
    const control = this.isTimeEntrySplit ? 'qty' : 'amount';
    return Number(this.billingForm?.get(control)?.value) || 0;
  }

  /** Per-case share (cent-floored preview); the backend distributes remainder cents to leading cases. */
  get splitPerCase(): number {
    if (this.splitPartsCount < 1) return 0;
    return Math.floor(Math.round(this.splitTotal * 100) / this.splitPartsCount) / 100;
  }

  get splitDividesEvenly(): boolean {
    if (this.splitPartsCount < 1) return true;
    return Math.round(this.splitTotal * 100) % this.splitPartsCount === 0;
  }

  onTargetsChange(selections: SearchSuggestionModel[]): void {
    this.billingForm?.get('target_case_ids')?.setValue(selections.map(s => s.display));
  }

  /** Description we auto-filled, so we can clear it again if the user leaves it untouched. */
  private autoFilledDescription: string | null = null;

  /**
   * When the user switches into Split/Duplicate mode with an empty description, suggest a sensible
   * default so the fanned-out entries aren't blank. Never overwrites a description the user typed,
   * and clears our own suggestion again if they switch back to "this case only" without editing it.
   */
  onSplitModeChange(mode: string): void {
    const descCtrl = this.billingForm?.get('description');
    if (!descCtrl) return;

    if (mode === this.SPLIT_MODE_THIS_CASE) {
      if (this.autoFilledDescription && descCtrl.value === this.autoFilledDescription) {
        descCtrl.setValue(null);
      }
      this.autoFilledDescription = null;
      return;
    }

    const current = (descCtrl.value as string | null)?.trim();
    if (!current) {
      const suggestion = mode === this.SPLIT_MODE_SPLIT
        ? (this.isTimeEntrySplit ? 'Time split across multiple cases' : 'Cost split across multiple cases')
        : 'Charge applied to multiple cases';
      descCtrl.setValue(suggestion);
      this.autoFilledDescription = suggestion;
    }
  }

  /** Returns the cost_type of the currently selected category, or null if Legacy/unknown/none */
  get selectedCategoryCostType(): string | null {
    const categoryId = this.billingForm?.get('category_id')?.value;
    if (!categoryId || categoryId === BillingFormComponent.LEGACY_CATEGORY_ID) return null;
    const category = this.categories?.find(c => c.id === categoryId);
    return category?.cost_type ?? null;
  }

  /**
   * Categories for flat fee display. The special Legacy bridge category stays pinned first; every
   * other category keeps the org's saved display order (built-ins and custom categories may be
   * interleaved — EE-211).
   */
  get flatFeeCategories(): BillableItemCategory[] {
    // When the org has disabled Legacy, drop it from the list entirely — unless we're editing an item
    // already stored as Legacy, where it must remain selectable so the item renders (Requirement 4.5).
    const legacy = this.effectiveLegacyAllowed
      ? this.categories.filter(cat => cat.id === BillingFormComponent.LEGACY_CATEGORY_ID)
      : [];
    // Hidden categories (EE-216) are excluded from the dropdown; Legacy is the special bridge and never hidden.
    const rest = this.categories.filter(cat => cat.id !== BillingFormComponent.LEGACY_CATEGORY_ID && !cat.hidden);
    return [...legacy, ...rest];
  }

  /** Categories for expense display: non-Legacy built-in + custom (exclude Legacy / flat_fee_only / hidden). */
  get expenseCategories(): BillableItemCategory[] {
    return this.categories.filter(cat => !cat.flat_fee_only && !cat.hidden);
  }

  /**
   * Categories for time-entry display. Time entries can pull from any non-flat-fee-only
   * category — the firm's custom categories plus non-Legacy built-ins. The category's
   * cost_type is metadata only; it never contributes to invoice hard/soft buckets for a
   * TIME_ENTRY item because the CostTypeResolver short-circuits non-expense items to null.
   */
  get timeEntryCategories(): BillableItemCategory[] {
    return this.categories.filter(cat => !cat.flat_fee_only && !cat.hidden);
  }

  /** Display function for the category autocomplete — shows category name given an ID */
  displayCategoryName(categoryId: string): string {
    if (!categoryId) return '';
    const cat = this.categories.find(c => c.id === categoryId);
    return cat?.name ?? '';
  }

  /** Called when a category is selected from the autocomplete */
  onCategorySelected(value: string) {
    this.billingForm.get('category_id').setValue(value);
  }

  /** Called on blur — resets the search control to match the actual category_id if no selection was made */
  onCategoryBlur() {
    // Defer to allow optionSelected to fire first
    setTimeout(() => {
      const currentCategoryId = this.billingForm.get('category_id')?.value;
      this.categorySearchCtrl.setValue(currentCategoryId || '', { emitEvent: false });
      this.filterCategories(currentCategoryId || '');
    });
  }

  /** Clears the selected category (for expense items where category is optional) */
  clearCategory() {
    this.billingForm.get('category_id').setValue(null);
    this.categorySearchCtrl.setValue('', { emitEvent: false });
    this.filterCategories('');
  }

  /** Display function for the payee autocomplete — shows payee name given an ID */
  displayPayeeName(payeeId: string): string {
    if (!payeeId) return '';
    const payee = this.payees.find(p => p.id === payeeId);
    return payee?.name ?? '';
  }

  /** Called when a saved payee is selected from the autocomplete */
  onPayeeSelected(payeeId: string) {
    const payee = this.payees.find(p => p.id === payeeId);
    if (payee) {
      this.billingForm.get('check_payee')?.setValue(payee.name);
      this.billingForm.get('check_payee_id')?.setValue(payee.id);
    }
  }

  /** Called on blur — handles free-text entry vs saved payee selection */
  onPayeeBlur() {
    setTimeout(() => {
      const currentPayeeId = this.billingForm.get('check_payee_id')?.value;
      const rawValue = this.payeeSearchCtrl.value || '';

      // If a saved payee is already selected and the control still holds its ID, keep the selection
      if (currentPayeeId) {
        const selectedPayee = this.payees.find(p => p.id === currentPayeeId);
        if (selectedPayee && (rawValue === currentPayeeId || rawValue === selectedPayee.name)) {
          this.payeeSearchCtrl.setValue(currentPayeeId, { emitEvent: false });
          this.filterPayees('');
          return;
        }
      }

      // Resolve the display value: if rawValue is a payee ID, get the name; otherwise use as-is
      const matchedPayee = this.payees.find(p => p.id === rawValue);
      if (matchedPayee) {
        // The control holds a payee ID (from a selection) but check_payee_id wasn't set yet —
        // treat as a saved payee selection
        this.billingForm.get('check_payee')?.setValue(matchedPayee.name);
        this.billingForm.get('check_payee_id')?.setValue(matchedPayee.id);
        this.payeeSearchCtrl.setValue(matchedPayee.id, { emitEvent: false });
        this.filterPayees('');
      } else {
        // Genuine free-text entry — rawValue is a name typed by the user
        this.billingForm.get('check_payee')?.setValue(rawValue || null);
        this.billingForm.get('check_payee_id')?.setValue(null);
        this.filterPayees(rawValue);
      }
    });
  }

  /** Filters payees based on the search text */
  private filterPayees(search: string) {
    const term = (search || '').toLowerCase();
    const isTypedText = !!term && !this.payees.some(p => p.id === search);

    this.filteredPayees = this.payees.filter(p => {
      if (!term || !isTypedText) return true;
      return p.name.toLowerCase().includes(term);
    });
  }

  /** Syncs the payee search control with the form's check_payee / check_payee_id values */
  private syncPayeeSearchCtrl() {
    const currentPayeeId = this.billingForm?.get('check_payee_id')?.value;
    const currentPayeeName = this.billingForm?.get('check_payee')?.value;

    if (currentPayeeId) {
      // Saved payee — set the search control to the payee ID (displayWith will show the name)
      this.payeeSearchCtrl.setValue(currentPayeeId, { emitEvent: false });
    } else if (currentPayeeName) {
      // Free-text payee — show the name directly
      this.payeeSearchCtrl.setValue(currentPayeeName, { emitEvent: false });
    } else {
      this.payeeSearchCtrl.setValue('', { emitEvent: false });
    }
    this.filterPayees(this.payeeSearchCtrl.value);

    // Listen for typing to filter the autocomplete list
    this.payeeSearchCtrl.valueChanges
      .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.filterPayees(value));
  }

  /** Sets the payee as free-text (from county fee settings, court, etc.) and syncs the autocomplete */
  private setFreeTextPayee(payeeName: string) {
    this.billingForm.get('check_payee')?.setValue(payeeName);
    this.billingForm.get('check_payee_id')?.setValue(null);
    this.payeeSearchCtrl.setValue(payeeName, { emitEvent: false });
    this.filterPayees(payeeName);
  }

  /** Sets the payee from a county fee setting payee ID and syncs the autocomplete */
  private setPayeeFromCountyFeeSetting(payeeId: string | undefined | null) {
    if (!payeeId) return;
    const payee = this.payees.find(p => p.id === payeeId);
    const payeeName = payee?.name ?? '';
    this.billingForm.get('check_payee')?.setValue(payeeName);
    this.billingForm.get('check_payee_id')?.setValue(payeeId);
    this.payeeSearchCtrl.setValue(payeeId, { emitEvent: false });
    this.filterPayees('');
  }

  /** Filters categories based on the search text and updates the filtered lists */
  private filterCategories(search: string) {
    const term = (search || '').toLowerCase();
    const isTypedText = !!term && !this.categories.some(c => c.id === search);

    const matchesFilter = (cat: BillableItemCategory) => {
      if (!term || !isTypedText) return true;
      return cat.name.toLowerCase().includes(term);
    };

    this.filteredFlatFeeCategories = this.flatFeeCategories.filter(matchesFilter);
    this.filteredExpenseCategories = this.expenseCategories.filter(matchesFilter);
    this.filteredTimeEntryCategories = this.timeEntryCategories.filter(matchesFilter);
  }

  // Subject to cancel form-specific subscriptions when form changes
  private formChanged$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges) {
    // Initialize billing descriptions once
    if (!this.allDescriptions$) {
      this.allDescriptions$ = this.organizationService.getBillingDescriptionHistory().pipe(
        map(descriptions => descriptions?.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })) ?? [])
      );
    }

    // Set up filtered descriptions when form is available
    if (changes['billingForm'] && this.billingForm) {
      const descriptionInput$ = this.billingForm.get('description').valueChanges.pipe(startWith(''));
      this.filteredDescriptions$ = combineLatest([this.allDescriptions$, descriptionInput$]).pipe(
        map(([descriptions, input]) => {
          const search = (input || '').trim().toLowerCase();
          if (!search) return descriptions;
          const tokens = search.split(/\s+/).filter(t => t.length > 0);
          return descriptions.filter(d => {
            const lower = d.toLowerCase();
            return tokens.every(token => lower.includes(token));
          });
        })
      );
    }

    // Re-setup form subscriptions when billingForm changes
    if (changes['billingForm']) {
      this.formChanged$.next();
      this.initializing = true;
      this.setupFormSubscriptions();
      this.initializeCategoryDefault();
      this.applyCategoryRequiredValidator();
      this.syncCategorySearchCtrl();
      this.syncPayeeSearchCtrl();
      this.initializing = false;
    }

    // React to a live org-setting toggle (RefreshLoggedInOrg propagates a new legacyBillingAllowed to
    // an already-open form). Re-apply the category validator and re-filter so a form doesn't keep stale
    // Legacy options/validators, and bump a still-Legacy NEW flat fee onto the default when disabled.
    if (changes['legacyBillingAllowed'] && !changes['legacyBillingAllowed'].isFirstChange() && this.billingForm) {
      this.applyCategoryRequiredValidator();
      if (!this.legacyBillingAllowed && !this.billableItemData) {
        const categoryControl = this.billingForm.get('category_id');
        const isFlatFee = this.billingForm.get('type')?.value === BillableItemType.FLAT_FEE;
        if (isFlatFee && categoryControl && (!categoryControl.value || categoryControl.value === BillingFormComponent.LEGACY_CATEGORY_ID)) {
          categoryControl.setValue(BillingFormComponent.DEFAULT_CATEGORY_ID);
        }
      }
      this.filterCategories(this.categorySearchCtrl.value || '');
    }

    // Update filtered categories when categories input changes
    if (changes['categories']) {
      this.filterCategories(this.categorySearchCtrl.value);
      this.syncCategorySearchCtrl();
    }

    // Update filtered payees when payees input changes
    if (changes['payees']) {
      this.filterPayees(this.payeeSearchCtrl.value);
    }

    // Load county fee settings when county changes
    if (this.county && changes['county']?.previousValue?.id !== changes['county']?.currentValue?.id) {
      this.countyFeeSettingsService.getByCounty(this.county)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (setting) => this.countyFeeSetting = setting,
          error: () => this.countyFeeSetting = null
        });
    }

    // Update payee if court or county changes while Request Check is checked (only for new items)
    if (!this.billableItemData && (changes['court'] || changes['county']) && this.billingForm.get('request_check')?.value) {
      this.updatePayeeFromCourtOrCounty();
    }
  }

  private setupFormSubscriptions() {
    // Type changes
    this.billingForm.get('type').valueChanges
      .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
      .subscribe((type: BillableItemType) => this.handleTypeChange(type));

    // Flat fee type changes
    this.billingForm.get('flat_fee_type').valueChanges
      .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
      .subscribe((type: FlatFeeType) => this.handleFlatFeeTypeChange(type));

    // Category changes
    const categoryControl = this.billingForm.get('category_id');
    if (categoryControl) {
      categoryControl.valueChanges
        .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
        .subscribe((categoryId: string | null) => this.handleCategoryChange(categoryId));
    }

    // Request check changes
    const requestCheckControl = this.billingForm.get('request_check');
    if (requestCheckControl) {
      this.updateCheckFieldsState(requestCheckControl.value);
      requestCheckControl.valueChanges
        .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
        .subscribe((checked: boolean) => this.updateCheckFieldsState(checked));
    }
  }

  /**
   * Sets the category_id default for new flat fees and existing items without a category.
   * - New flat fees: default to Legacy (or Client Charge when the org has Legacy disabled)
   * - Existing flat fees with no category_id (pre-existing data): default to Legacy (Req 11.1) — this
   *   is preserved even at a Legacy-disabled org via `effectiveLegacyAllowed` (no data loss, Req 4.5)
   * - Existing flat fees with a category_id: keep the stored value (Req 11.2, 11.3)
   */
  private initializeCategoryDefault() {
    const categoryControl = this.billingForm?.get('category_id');
    const typeValue = this.billingForm?.get('type')?.value;
    if (!categoryControl || typeValue !== BillableItemType.FLAT_FEE) return;

    // Only default if category_id is currently null/empty
    if (!categoryControl.value) {
      categoryControl.setValue(this.defaultFlatFeeCategoryId, { emitEvent: false });
    }
  }

  /** Legacy by default; a generic Client Charge when the org has disabled Legacy for new entries. */
  private get defaultFlatFeeCategoryId(): string {
    return this.effectiveLegacyAllowed
      ? BillingFormComponent.LEGACY_CATEGORY_ID
      : BillingFormComponent.DEFAULT_CATEGORY_ID;
  }

  /**
   * At a Legacy-disabled org a category is mandatory for every entry type (Req 4.4) — this also keeps
   * the client in step with the backend guard, which rejects any uncategorized manual entry there. When
   * Legacy is allowed the control has no category validator (category stays optional, as it is today).
   */
  private applyCategoryRequiredValidator() {
    const categoryControl = this.billingForm?.get('category_id');
    if (!categoryControl) return;
    if (this.legacyBillingAllowed) {
      categoryControl.clearValidators();
    } else {
      categoryControl.setValidators(Validators.required);
    }
    categoryControl.updateValueAndValidity({ emitEvent: false });
  }

  /** Syncs the category search control with the form's category_id value */
  private syncCategorySearchCtrl() {
    // Set initial value from form
    const currentCategoryId = this.billingForm?.get('category_id')?.value;
    this.categorySearchCtrl.setValue(currentCategoryId || '', { emitEvent: false });
    this.filterCategories(currentCategoryId || '');

    // Listen for typing to filter the autocomplete list
    this.categorySearchCtrl.valueChanges
      .pipe(takeUntil(this.formChanged$), takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.filterCategories(value));
  }

  private updatePayeeFromCourtOrCounty() {
    if (this.billableItemData) return; // Don't auto-fill payee when editing an existing item
    const currentPayee = this.billingForm.get('check_payee')?.value;
    if (currentPayee) return;
    const payeeName = this.court?.name || this.county?.name;
    if (payeeName) {
      this.setFreeTextPayee(payeeName);
    }
  }

  private updateCheckFieldsState(requestCheck: boolean) {
    const checkFields = ['check_payee', 'check_payee_id', 'check_processed_date', 'check_processed_by', 'check_denied'];

    checkFields.forEach(fieldName => {
      const control = this.billingForm.get(fieldName);
      if (!control) return;

      if (requestCheck) {
        control.enable();
        if (fieldName === 'check_payee') {
          this.updatePayeeFromCourtOrCounty();
        }
      } else {
        control.disable();
        if (fieldName === 'check_denied') {
          control.setValue(false);
        } else {
          control.setValue(null);
        }
        if (fieldName === 'check_payee') {
          this.payeeSearchCtrl.setValue('', { emitEvent: false });
          this.filterPayees('');
        }
      }
    });
  }

  public onPerformedByChange(userId: string) {
    const member = this.teamMembers.find(m => m.id === userId);
    if (member) {
      this.billingForm.patchValue({
        performed_by: member.display_name,
        performed_by_user_id: member.id
      });
    }
  }

  /**
   * Handles the per-item Cost Type toggle. Translates the "None" sentinel into a real null on
   * the form so the backend CostTypeResolver treats the item as falling back to the category
   * default (for expenses) or as non-applicable (for fees/time entries).
   */
  public onCostTypeToggleChange(value: string | null) {
    const costType = value === this.COST_TYPE_NONE ? null : value;
    this.billingForm.get('cost_type')?.setValue(costType);
  }

  public handleTypeChange(type: BillableItemType) {
    this.resetForm(type);
  }

  private handleFlatFeeTypeChange(newFlatFeeType: FlatFeeType) {
    const amount = this.billingForm.get('amount');
    const description = this.billingForm.get('description');

    if (newFlatFeeType === FlatFeeType.CLIENT_ATTORNEY_FEE) {
      amount.setValue(this.attorneyFee ?? 0);
      this.applyDerivedAmountLock(amount);
      description.setValue('Attorney Fee');
    } else if (newFlatFeeType === FlatFeeType.COUNTY_FEE && (this.setupForm || (this.county && this.countyFeeSetting && this.tenants))) {
      description.setValue(`${newFlatFeeType} - ${this.county?.name ?? '[COUNTY]'}`);
      amount.setValue(this.setupForm ? 0 : BillingFormComponent.calculateCountyFeeAmount(this.countyFeeSetting, this.tenants));
      this.applyDerivedAmountLock(amount);
      if (this.countyFeeSetting?.county_fee_request_check) {
        this.billingForm.get('request_check')?.setValue(true);
        this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.county_fee_payee_id);
      }
    } else if (newFlatFeeType === FlatFeeType.WRIT_FEE && (this.setupForm || (this.county && this.countyFeeSetting && this.tenants))) {
      description.setValue(`${newFlatFeeType} - ${this.county?.name ?? '[COUNTY]'}`);
      amount.setValue(this.setupForm ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.writ_fee, this.countyFeeSetting?.writ_additional_tenant_fee, this.tenants));
      this.applyDerivedAmountLock(amount);
      if (this.countyFeeSetting?.writ_fee_request_check) {
        this.billingForm.get('request_check')?.setValue(true);
        this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.writ_fee_payee_id);
      }
    } else if (newFlatFeeType === FlatFeeType.SHERIFF_FEE && (this.setupForm || (this.county && this.countyFeeSetting && this.tenants))) {
      description.setValue(`${newFlatFeeType} - ${this.county?.name ?? '[COUNTY]'}`);
      amount.setValue(this.setupForm ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.sheriff_fee, this.countyFeeSetting?.sheriff_additional_tenant_fee, this.tenants));
      this.applyDerivedAmountLock(amount);
      if (this.countyFeeSetting?.sheriff_fee_request_check) {
        this.billingForm.get('request_check')?.setValue(true);
        this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.sheriff_fee_payee_id);
      }
    } else if (newFlatFeeType === FlatFeeType.HEARING_FEE && (this.setupForm || (this.county && this.countyFeeSetting && this.tenants))) {
      description.setValue(`${newFlatFeeType} - ${this.county?.name ?? '[COUNTY]'}`);
      amount.setValue(this.setupForm ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.hearing_fee, this.countyFeeSetting?.hearing_additional_tenant_fee, this.tenants));
      this.applyDerivedAmountLock(amount);
      if (this.countyFeeSetting?.hearing_fee_request_check) {
        this.billingForm.get('request_check')?.setValue(true);
        this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.hearing_fee_payee_id);
      }
    } else {
      amount.setValue(null);
      amount.enable();
      description.setValue('');
    }
  }

  /**
   * A built-in fee's Amount is derived from settings rather than typed, but "derived" only means
   * "read-only" in the workflow builder: there an auto-billable is defined with no case in front of
   * it, so the field holds a placeholder 0 and the real figure is resolved when the step completes.
   * Everywhere a real case is on screen — the eviction action center, the billable-item edit dialog,
   * the bulk Add Billable dialog — the pre-filled amount is a starting point staff must be able to
   * overwrite, or a client with no configured attorney fee strands them on a greyed-out $0.
   */
  private applyDerivedAmountLock(amount: AbstractControl) {
    if (this.setupForm || this.isWorkflowConfig) {
      amount.disable();
    } else {
      amount.enable();
    }
  }

  private handleCategoryChange(categoryId: string | null) {
    // Sync the autocomplete display
    this.categorySearchCtrl.setValue(categoryId || '', { emitEvent: false });

    if (!categoryId || categoryId === BillingFormComponent.LEGACY_CATEGORY_ID) {
      // Legacy mode: restore FlatFeeType dropdown behavior
      const flatFeeTypeControl = this.billingForm.get('flat_fee_type');
      if (this.billingForm.get('type').value === BillableItemType.FLAT_FEE && !flatFeeTypeControl.value) {
        flatFeeTypeControl.setValue(FlatFeeType.CUSTOM);
      }
      // Clear cost_type in Legacy mode
      if (!this.initializing) {
        this.billingForm.get('cost_type')?.setValue(null);
      }
    } else {
      // Non-Legacy category: hide FlatFeeType dropdown, clear flat_fee_type
      if (this.billingForm.get('type').value === BillableItemType.FLAT_FEE) {
        this.billingForm.get('flat_fee_type').setValue(null);
      }

      const description = this.billingForm.get('description');

      // Set cost_type from category default (only when user changes category, not on initial load of existing item)
      if (!this.initializing || !this.billableItemData) {
        const category = this.categories.find(c => c.id === categoryId);
        if (category?.cost_type) {
          this.billingForm.get('cost_type')?.setValue(category.cost_type);
        }
      }

      const amount = this.billingForm.get('amount');
      const isWorkflow = this.setupForm || this.isWorkflowConfig;
      const countyName = this.county?.name ?? '[COUNTY]';
      const hasCountyData = isWorkflow || (this.county && this.countyFeeSetting && this.tenants);

      switch (categoryId) {
        case 'builtin-county-fee':
          if (hasCountyData) {
            amount.setValue(isWorkflow ? 0 : BillingFormComponent.calculateCountyFeeAmount(this.countyFeeSetting, this.tenants));
            this.applyDerivedAmountLock(amount);
            description.setValue(`County Fee - ${countyName}`);
            if (this.countyFeeSetting?.county_fee_request_check) {
              this.billingForm.get('request_check')?.setValue(true);
              this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.county_fee_payee_id);
            }
          } else {
            amount.setValue(null);
            amount.enable();
          }
          break;

        case 'builtin-writ-filing-fee':
          if (hasCountyData) {
            amount.setValue(isWorkflow ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.writ_fee, this.countyFeeSetting?.writ_additional_tenant_fee, this.tenants));
            this.applyDerivedAmountLock(amount);
            description.setValue(`Writ Filing Fee - ${countyName}`);
            if (this.countyFeeSetting?.writ_fee_request_check) {
              this.billingForm.get('request_check')?.setValue(true);
              this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.writ_fee_payee_id);
            }
          } else {
            amount.setValue(null);
            amount.enable();
          }
          break;

        case 'builtin-sheriff-fee':
          if (hasCountyData) {
            amount.setValue(isWorkflow ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.sheriff_fee, this.countyFeeSetting?.sheriff_additional_tenant_fee, this.tenants));
            this.applyDerivedAmountLock(amount);
            description.setValue(`Sheriff Fee - ${countyName}`);
            if (this.countyFeeSetting?.sheriff_fee_request_check) {
              this.billingForm.get('request_check')?.setValue(true);
              this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.sheriff_fee_payee_id);
            }
          } else {
            amount.setValue(null);
            amount.enable();
          }
          break;

        case 'builtin-hearing-fee':
          if (hasCountyData) {
            amount.setValue(isWorkflow ? 0 : BillingFormComponent.calculateFeeAmount(this.countyFeeSetting?.hearing_fee, this.countyFeeSetting?.hearing_additional_tenant_fee, this.tenants));
            this.applyDerivedAmountLock(amount);
            description.setValue(`Hearing Fee - ${countyName}`);
            if (this.countyFeeSetting?.hearing_fee_request_check) {
              this.billingForm.get('request_check')?.setValue(true);
              this.setPayeeFromCountyFeeSetting(this.countyFeeSetting.hearing_fee_payee_id);
            }
          } else {
            amount.setValue(null);
            amount.enable();
          }
          break;

        case 'builtin-attorney-fee':
          amount.setValue(this.attorneyFee ?? 0);
          this.applyDerivedAmountLock(amount);
          description.setValue('Attorney Fee');
          break;

        default:
          // Client Charge or custom category: keep fields editable
          amount.enable();
          break;
      }
    }
  }

  public static calculateFeeAmount(
    baseFee: number | undefined,
    perTenantFee: number | undefined,
    tenants: Tenant[]
  ): number | null {
    if (!baseFee || !tenants?.length) return null;
    const additionalFees = perTenantFee ? perTenantFee * (tenants.length - 1) : 0;
    return baseFee + Math.max(0, additionalFees);
  }

  public static calculateCountyFeeAmount(countySetting: CountyFeeSetting, tenants: Tenant[]): number | null {
    return BillingFormComponent.calculateFeeAmount(countySetting?.fee, countySetting?.additional_tenant_fee, tenants);
  }

  private resetForm(type: BillableItemType) {
    const form = this.billingForm;

    Object.keys(form.controls).forEach(key => {
      if (['type', 'date_incurred', 'description', 'performed_by', 'performed_by_user_id'].includes(key)) return;

      const control = form.get(key);
      if (type === BillableItemType.TIME_ENTRY && ['qty', 'rate'].includes(key)) {
        control.setValue(null);
        control.setValidators(Validators.required);
      } else if (type === BillableItemType.FLAT_FEE && key === 'flat_fee_type') {
        control.setValue(FlatFeeType.CUSTOM);
        control.setValidators(Validators.required);
      } else if (type === BillableItemType.FLAT_FEE && key === 'category_id') {
        control.setValue(this.defaultFlatFeeCategoryId);
        // At a Legacy-disabled org a real category is mandatory (Req 4.4).
        if (this.legacyBillingAllowed) control.clearValidators();
        else control.setValidators(Validators.required);
      } else {
        if (key === 'amount') {
          control.setValue(null);
          control.enable();
        }
        if (key === 'category_id') {
          control.setValue(null);
        }
        // The backend guard rejects any uncategorized manual entry at a Legacy-disabled org, so require
        // a category for every entry type there; elsewhere category stays optional.
        if (key === 'category_id' && !this.legacyBillingAllowed) {
          control.setValidators(Validators.required);
        } else {
          control.clearValidators();
        }
      }
      control.updateValueAndValidity();
    });

    form.get('save_description').setValue(false);
    form.get('description').setValue('');

    // Description is always optional — no validator toggling needed.

    // Sync category autocomplete with the new category_id value
    const newCategoryId = form.get('category_id')?.value;
    this.categorySearchCtrl.reset(newCategoryId || '', { emitEvent: false });
    this.filterCategories(newCategoryId || '');

    if (type !== BillableItemType.TIME_ENTRY) {
      form.get('amount').setValidators(Validators.required);
      form.get('amount').updateValueAndValidity();
    }

    form.updateValueAndValidity();
  }
}
